import { getBrowseLayout } from "@/components/BrowseLayout";
import UnitSignup, { UNIT_SIGNUP_FRAGMENT } from "@/components/UnitSignup";
import { ApartmentSignupDocument, EnitreSignupStaticPathsDocument, UnitType } from "@/generated/graphql";
import { AMPLITUDE_EVENTS, logEvent } from "@/lib/amplitude";
import { graphqlFetch } from "@/lib/useGraphql";
import { getApartmentImages } from "@/utils/getApartmentImages";
import { gql } from "graphql-tag";
import { GetStaticPaths, GetStaticPropsContext, InferGetStaticPropsType } from "next";
import * as React from "react";

export const APARTMENT_SIGNUP = gql`
  query ApartmentSignup($apartmentSlug: String!) {
    listing(apartmentSlug: $apartmentSlug) {
      ...UnitSignup
      unitType
      apartmentCmsData
    }
  }
  ${UNIT_SIGNUP_FRAGMENT}
`;

export const getStaticProps = async ({ params }: GetStaticPropsContext) => {
  const apartmentSlug = params?.apartment as string;
  const { listing } = await graphqlFetch(ApartmentSignupDocument, { apartmentSlug }, { fetchPolicy: "cache-first" });

  if (!listing || listing.unitType === UnitType.Shared) {
    return { props: { listing: null }, notFound: true };
  }
  return {
    props: {
      listing,
    },
    revalidate: 3600,
  };
};

export const ENTIRE_SIGNUP_STATIC_PATHS = gql`
  query EnitreSignupStaticPaths($unitType: UnitType) {
    listings(unitType: $unitType) {
      citySlug
      apartmentSlug
    }
  }
`;

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const { listings } = await graphqlFetch(EnitreSignupStaticPathsDocument, { unitType: UnitType.Entire });

  const paths = listings.flatMap((listing) => {
    return (locales ?? []).map((locale) => ({
      params: { apartment: listing.apartmentSlug, uid: listing.citySlug },
      locale,
    }));
  });

  return {
    paths,
    fallback: "blocking",
  };
};

export default function EntireUnitSignup({ listing }: InferGetStaticPropsType<typeof getStaticProps>) {
  React.useEffect(() => {
    if (listing) {
      logEvent(AMPLITUDE_EVENTS.signupPageView, { apartmentId: listing.apartmentSlug });
    }
  }, [listing]);

  if (!listing) return null;

  const images = getApartmentImages({ apartment: listing, width: 900 });

  return <UnitSignup listing={listing} unitType={UnitType.Entire} images={images} />;
}

EntireUnitSignup.getLayout = getBrowseLayout;
